import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MapPin, Phone, Mail, Star, Trophy, Users, TrendingUp, CheckCircle, Award } from 'lucide-react';

const LocationSEO = () => {
  const locations = [
    {
      area: 'Karol Bagh',
      address: 'Vidya365, Karol Bagh, New Delhi - 110005',
      phone: '+91 85041 10365',
      nearbyAreas: ['Rajendra Place', 'Patel Nagar', 'Dev Nagar', 'Ranjeet Nagar', 'Bapa Nagar']
    }
  ];

  const keywords = [
    { term: 'Best Bank Coaching in Karol Bagh', rank: '#1', students: '200+' },
    { term: 'Best SSC Coaching in Karol Bagh', rank: '#1', students: '150+' },
    { term: 'Best RBI Grade B Coaching in Karol Bagh', rank: '#1', students: '50+' },
    { term: 'Top Bank Coaching in Delhi', rank: 'Top 3', students: '500+' },
    { term: 'IBPS Coaching Near Karol Bagh', rank: '#1', students: '180+' },
    { term: 'SBI PO Coaching Karol Bagh', rank: '#1', students: '120+' }
  ];

  const achievements = [
    '500+ Selections in Government Exams',
    '85% Success Rate - Highest in Delhi',
    '4.6★ Google Rating (263+ Reviews)',
    '10,000+ Students Trained',
    'Expert Faculty with 15+ Years Experience',
    '100% Job-Oriented Training'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* SEO-Optimized Hero */}
      <div className="bg-gradient-to-br from-red-600 via-red-700 to-orange-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Best Bank, SSC & RBI Grade B Coaching in Karol Bagh, Delhi
            </h1>
            <p className="text-2xl mb-6">Top-Rated Coaching Institute | 500+ Selections | 85% Success Rate</p>
            <div className="flex flex-wrap justify-center gap-6 text-lg">
              <div className="flex items-center gap-2">
                <MapPin className="w-6 h-6" />
                <span>Karol Bagh, New Delhi</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-6 h-6" />
                <span>+91 85041 10365</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                <span>4.6★ Rating (263+ Reviews)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why #1 in Karol Bagh */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">
            Why Vidya365 is the #1 Coaching Institute in Karol Bagh?
          </h2>
          <p className="text-xl text-gray-600">Trusted by 10,000+ Students Across Delhi NCR</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <Card className="text-center hover:shadow-xl transition-shadow border-2 border-red-200">
            <CardContent className="p-8">
              <Trophy className="w-16 h-16 mx-auto mb-4 text-yellow-600" />
              <h3 className="text-2xl font-bold text-gray-800 mb-2">500+</h3>
              <p className="text-gray-700 font-semibold">Total Selections</p>
              <p className="text-sm text-gray-600 mt-2">In IBPS, SBI, SSC, RBI exams</p>
            </CardContent>
          </Card>

          <Card className="text-center hover:shadow-xl transition-shadow border-2 border-green-200">
            <CardContent className="p-8">
              <TrendingUp className="w-16 h-16 mx-auto mb-4 text-green-600" />
              <h3 className="text-2xl font-bold text-gray-800 mb-2">85%</h3>
              <p className="text-gray-700 font-semibold">Success Rate</p>
              <p className="text-sm text-gray-600 mt-2">Highest in Karol Bagh area</p>
            </CardContent>
          </Card>

          <Card className="text-center hover:shadow-xl transition-shadow border-2 border-blue-200">
            <CardContent className="p-8">
              <Users className="w-16 h-16 mx-auto mb-4 text-blue-600" />
              <h3 className="text-2xl font-bold text-gray-800 mb-2">10,000+</h3>
              <p className="text-gray-700 font-semibold">Students Trained</p>
              <p className="text-sm text-gray-600 mt-2">Since establishment</p>
            </CardContent>
          </Card>
        </div>

        {/* Keyword Rankings */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h3 className="text-3xl font-bold text-gray-800 mb-6 text-center">
            Top-Ranked Coaching Institute for These Searches
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {keywords.map((kw, idx) => (
              <div key={idx} className="bg-gradient-to-r from-green-50 to-teal-50 p-4 rounded-lg border-l-4 border-green-500">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-bold text-gray-800">{kw.term}</p>
                    <p className="text-sm text-gray-600">{kw.students} students enrolled</p>
                  </div>
                  <div className="bg-green-600 text-white px-4 py-2 rounded-full font-bold">
                    {kw.rank}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Courses Offered */}
        <div className="mb-12">
          <h2 className="text-4xl font-bold text-gray-800 mb-8 text-center">
            Best Coaching for Bank & SSC Exams in Karol Bagh
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Bank Exams */}
            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-blue-700 mb-4">Bank Exam Coaching</h3>
                <ul className="space-y-3">
                  {[
                    'SBI PO & SBI Clerk Coaching',
                    'IBPS PO & IBPS Clerk Coaching',
                    'IBPS RRB PO & Clerk Coaching',
                    'RBI Grade B Coaching',
                    'NABARD Grade A Coaching',
                    'Bank of Baroda PO Coaching'
                  ].map((course, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{course}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* SSC Exams */}
            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-purple-700 mb-4">SSC Exam Coaching</h3>
                <ul className="space-y-3">
                  {[
                    'SSC CGL Coaching',
                    'SSC CHSL Coaching',
                    'SSC MTS Coaching',
                    'SSC CPO Coaching',
                    'SSC GD Constable Coaching',
                    'SSC Stenographer Coaching'
                  ].map((course, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{course}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Location Details */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-2xl p-12 mb-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Visit Our Karol Bagh Center</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold mb-4">Address</h3>
                <p className="text-lg mb-6">{locations[0].address}</p>
                
                <h3 className="text-2xl font-bold mb-4">Contact</h3>
                <p className="text-lg mb-2">📞 {locations[0].phone}</p>
                <p className="text-lg mb-6">📧 info@vidya365.com</p>

                <Link to="/contact">
                  <Button className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
                    Get Directions
                  </Button>
                </Link>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4">Nearby Areas We Serve</h3>
                <ul className="space-y-2">
                  {locations[0].nearbyAreas.map((area, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-lg">
                      <MapPin className="w-5 h-5" />
                      {area}
                    </li>
                  ))}
                  <li className="flex items-center gap-2 text-lg">
                    <MapPin className="w-5 h-5" />
                    All Delhi NCR
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Achievements */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className="text-4xl font-bold text-gray-800 mb-8 text-center">
            What Makes Us the Best Coaching in Delhi
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {achievements.map((achievement, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-green-50 p-4 rounded-lg">
                <Award className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <span className="text-gray-700 font-semibold">{achievement}</span>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section for SEO */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-4xl font-bold text-gray-800 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Q: Which is the best bank coaching in Karol Bagh?
              </h3>
              <p className="text-gray-700">
                Vidya365 is recognized as the best bank coaching institute in Karol Bagh with 500+ selections, 85% success rate, and expert faculty. We offer coaching for IBPS PO, SBI PO, RBI Grade B, and all major bank exams.
              </p>
            </div>

            <div className="border-b pb-4">
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Q: Which is the top SSC coaching in Delhi?
              </h3>
              <p className="text-gray-700">
                Vidya365 is among the top SSC coaching institutes in Delhi, located in Karol Bagh. We provide comprehensive coaching for SSC CGL, CHSL, MTS, CPO, and all SSC exams with proven results.
              </p>
            </div>

            <div className="border-b pb-4">
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Q: Where is the best RBI Grade B coaching in Karol Bagh?
              </h3>
              <p className="text-gray-700">
                Vidya365 offers the best RBI Grade B coaching in Karol Bagh, Delhi with specialized faculty, comprehensive study material, and Phase 1, Phase 2 & Interview preparation.
              </p>
            </div>

            <div className="border-b pb-4">
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Q: Do you offer both online and offline classes?
              </h3>
              <p className="text-gray-700">
                Yes, Vidya365 offers both offline classes at our Karol Bagh center and live online classes. You can choose the mode that suits you best.
              </p>
            </div>

            <div className="pb-4">
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Q: What is your success rate?
              </h3>
              <p className="text-gray-700">
                Vidya365 has an 85% success rate, which is the highest among coaching institutes in Karol Bagh and Delhi. We have 500+ selections in various government exams.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Join the #1 Coaching Institute in Karol Bagh Today!
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Limited seats available - Enroll now for upcoming batches
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/student/register">
              <Button className="bg-red-600 hover:bg-red-700 text-white px-10 py-6 text-lg">
                Register Now
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-red-600 text-red-600 hover:bg-red-50 px-10 py-6 text-lg">
                Visit Our Center
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LocationSEO;
